import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Package,
  Search,
  Download,
  Plus,
  Eye,
  Loader2,
  Save,
  Trash2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import complianceService from '../services/complianceService';
import * as XLSX from 'xlsx';

export default function ProductManagementPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const emptyProduct = {
    name: '',
    description: '',
    sku: '',
    categoryId: '',
    brandName: '',
    manufacturer: '',
    unit: '',
    thcContent: '',
    cbdContent: '',
    strain: '',
    batchNumber: '',
    quantity: '',
    minQuantity: '',
    status: 'Active',
  };
  const [newProduct, setNewProduct] = useState(emptyProduct);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.allSettled([
        complianceService.getProducts(),
        complianceService.getProductCategories(),
      ]);
      if (productsData.status === 'fulfilled') {
        setProducts(Array.isArray(productsData.value) ? productsData.value : []);
      }
      if (categoriesData.status === 'fulfilled') {
        setCategories(Array.isArray(categoriesData.value) ? categoriesData.value : []);
      }
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...newProduct,
        quantity: newProduct.quantity ? Number(newProduct.quantity) : undefined,
        minQuantity: newProduct.minQuantity ? Number(newProduct.minQuantity) : undefined,
        categoryId: newProduct.categoryId || undefined,
      };
      await complianceService.createProduct(payload);
      toast.success('Product created successfully');
      setIsCreateDialogOpen(false);
      setNewProduct(emptyProduct);
      fetchData();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProduct?.id) return;
    setIsSaving(true);
    try {
      await complianceService.updateProduct(selectedProduct.id, editData);
      toast.success('Product updated successfully');
      setIsEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await complianceService.deleteProduct(id);
      toast.success('Product deleted');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete product');
    }
  };

  const handleExport = () => {
    try {
      const exportData = filteredProducts.map(p => ({
        'SKU': p.sku,
        'Name': p.name,
        'Brand': p.brandName,
        'Manufacturer': p.manufacturer,
        'Category': getCategoryName(p.categoryId),
        'Strain': p.strain,
        'THC': p.thcContent,
        'CBD': p.cbdContent,
        'Quantity': p.quantity,
        'Unit': p.unit,
        'Status': p.status,
        'Batch': p.batchNumber,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      XLSX.writeFile(wb, `products_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Products exported successfully');
    } catch {
      toast.error('Failed to export');
    }
  };

  const viewDetails = (product) => {
    setSelectedProduct(product);
    setEditData({ ...product });
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'N/A';
    const cat = categories.find(c =>
      c.productCategoryId === categoryId ||
      c.productCategoryId === Number(categoryId) ||
      c.id === categoryId ||
      c.id === String(categoryId)
    );
    return cat?.name ?? String(categoryId);
  };

  // Flatten category tree for hierarchical select
  const flatCategories = (() => {
    const flat = [];
    const buildFlat = (items, depth = 0, parentId = null) => {
      const children = items.filter(c => (c.parentCategoryId ?? null) === parentId);
      for (const cat of children) {
        flat.push({ ...cat, depth });
        buildFlat(items, depth + 1, cat.productCategoryId ?? cat.id);
      }
    };
    // Build hierarchy; fall back to flat list if no parentCategoryId field
    const hasHierarchy = categories.some(c => c.parentCategoryId !== undefined);
    if (hasHierarchy) {
      buildFlat(categories);
      // Append any that didn't get placed (safety)
      const placed = new Set(flat.map(c => c.productCategoryId ?? c.id));
      categories.forEach(c => { if (!placed.has(c.productCategoryId ?? c.id)) flat.push({ ...c, depth: 0 }); });
    } else {
      categories.forEach(c => flat.push({ ...c, depth: 0 }));
    }
    return flat;
  })();

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === 'active') return <Badge variant="success">Active</Badge>;
    if (s === 'low stock') return <Badge className="bg-orange-500 text-white">Low Stock</Badge>;
    if (s === 'out of stock') return <Badge variant="destructive">Out of Stock</Badge>;
    return <Badge variant="secondary">{status ?? 'N/A'}</Badge>;
  };

  const filteredProducts = products.filter(p => {
    const matchSearch =
      (p.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (p.sku?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (p.batchNumber?.toLowerCase() ?? '').includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === 'all' || String(p.categoryId) === categoryFilter;
    return matchSearch && matchCategory;
  });

  const summaryCards = [
    { title: 'Total Products', value: products.length, icon: Package, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { title: 'Categories', value: categories.length, icon: Filter, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { title: 'Active', value: products.filter(p => p.status?.toLowerCase() === 'active').length, icon: Package, color: 'text-green-600', bgColor: 'bg-green-50' },
    { title: 'Low Stock', value: products.filter(p => p.status?.toLowerCase() === 'low stock').length, icon: Package, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Product Management</h1>
            <p className="text-muted-foreground mt-1">Manage your cannabis product catalog</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Products Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Product Catalog</CardTitle>
                <CardDescription>All registered products ({filteredProducts.length} records)</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, SKU, or batch number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {categories.length > 0 && (
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {flatCategories.map(cat => {
                      const id = String(cat.productCategoryId ?? cat.id);
                      return (
                        <SelectItem key={id} value={id}>
                          {'  '.repeat(cat.depth)}{cat.depth > 0 ? '↳ ' : ''}{cat.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No products found. Add your first product to get started.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Strain</TableHead>
                      <TableHead>THC</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.sku || 'N/A'}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{getCategoryName(product.categoryId)}</TableCell>
                        <TableCell>{product.strain || 'N/A'}</TableCell>
                        <TableCell>{product.thcContent || 'N/A'}</TableCell>
                        <TableCell>
                          {product.quantity != null ? `${product.quantity} ${product.unit || ''}`.trim() : 'N/A'}
                        </TableCell>
                        <TableCell>{product.batchNumber || 'N/A'}</TableCell>
                        <TableCell>{getStatusBadge(product.status)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => viewDetails(product)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View / Edit Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Product Details</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Edit product information' : 'Complete product information'}
            </DialogDescription>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              {isEditMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Name *</Label>
                    <Input value={editData.name ?? ''} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>SKU</Label>
                    <Input value={editData.sku ?? ''} onChange={e => setEditData(p => ({ ...p, sku: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Category</Label>
                    <Select value={String(editData.categoryId ?? '')} onValueChange={v => setEditData(p => ({ ...p, categoryId: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {flatCategories.map(cat => {
                          const id = String(cat.productCategoryId ?? cat.id);
                          return (
                            <SelectItem key={id} value={id}>
                              {'  '.repeat(cat.depth)}{cat.depth > 0 ? '↳ ' : ''}{cat.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Brand Name</Label>
                    <Input value={editData.brandName ?? ''} onChange={e => setEditData(p => ({ ...p, brandName: e.target.value }))} placeholder="e.g. Green Label" />
                  </div>
                  <div className="space-y-1">
                    <Label>Manufacturer</Label>
                    <Input value={editData.manufacturer ?? ''} onChange={e => setEditData(p => ({ ...p, manufacturer: e.target.value }))} placeholder="e.g. Acme Cannabis Co." />
                  </div>
                  <div className="space-y-1">
                    <Label>Strain</Label>
                    <Input value={editData.strain ?? ''} onChange={e => setEditData(p => ({ ...p, strain: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>THC Content</Label>
                    <Input value={editData.thcContent ?? ''} onChange={e => setEditData(p => ({ ...p, thcContent: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>CBD Content</Label>
                    <Input value={editData.cbdContent ?? ''} onChange={e => setEditData(p => ({ ...p, cbdContent: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Quantity</Label>
                    <Input type="number" value={editData.quantity ?? ''} onChange={e => setEditData(p => ({ ...p, quantity: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Unit</Label>
                    <Input value={editData.unit ?? ''} onChange={e => setEditData(p => ({ ...p, unit: e.target.value }))} placeholder="grams, units, bottles..." />
                  </div>
                  <div className="space-y-1">
                    <Label>Batch Number</Label>
                    <Input value={editData.batchNumber ?? ''} onChange={e => setEditData(p => ({ ...p, batchNumber: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select value={editData.status ?? 'Active'} onValueChange={v => setEditData(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Low Stock">Low Stock</SelectItem>
                        <SelectItem value="Out of Stock">Out of Stock</SelectItem>
                        <SelectItem value="Discontinued">Discontinued</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label>Description</Label>
                    <Textarea value={editData.description ?? ''} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))} rows={3} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Name</p><p className="font-medium">{selectedProduct.name}</p></div>
                  <div><p className="text-sm text-muted-foreground">SKU</p><p className="font-medium">{selectedProduct.sku || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Category</p><p className="font-medium">{getCategoryName(selectedProduct.categoryId)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Brand Name</p><p className="font-medium">{selectedProduct.brandName || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Manufacturer</p><p className="font-medium">{selectedProduct.manufacturer || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Strain</p><p className="font-medium">{selectedProduct.strain || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">THC Content</p><p className="font-medium">{selectedProduct.thcContent || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">CBD Content</p><p className="font-medium">{selectedProduct.cbdContent || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Quantity</p><p className="font-medium">{selectedProduct.quantity != null ? `${selectedProduct.quantity} ${selectedProduct.unit || ''}`.trim() : 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Min Quantity</p><p className="font-medium">{selectedProduct.minQuantity ?? 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Batch Number</p><p className="font-medium">{selectedProduct.batchNumber || 'N/A'}</p></div>
                  <div><p className="text-sm text-muted-foreground">Status</p><div className="mt-1">{getStatusBadge(selectedProduct.status)}</div></div>
                  {selectedProduct.description && (
                    <div className="col-span-2"><p className="text-sm text-muted-foreground">Description</p><p className="text-sm">{selectedProduct.description}</p></div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t">
                {isEditMode ? (
                  <>
                    <Button className="flex-1" onClick={handleUpdate} disabled={isSaving}>
                      {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => setIsEditMode(false)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button className="flex-1" onClick={() => setIsEditMode(true)}>Edit Product</Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedProduct.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>Enter the product details below</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Product Name *</Label>
                <Input required value={newProduct.name} onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>SKU</Label>
                <Input value={newProduct.sku} onChange={e => setNewProduct(p => ({ ...p, sku: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={newProduct.categoryId} onValueChange={v => setNewProduct(p => ({ ...p, categoryId: v }))} disabled={categories.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={categories.length === 0 ? 'No categories available' : 'Select category'} />
                  </SelectTrigger>
                  <SelectContent>
                    {flatCategories.map(cat => {
                      const id = String(cat.productCategoryId ?? cat.id);
                      return (
                        <SelectItem key={id} value={id}>
                          {'  '.repeat(cat.depth)}{cat.depth > 0 ? '↳ ' : ''}{cat.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Brand Name</Label>
                <Input value={newProduct.brandName} onChange={e => setNewProduct(p => ({ ...p, brandName: e.target.value }))} placeholder="e.g. Green Label" />
              </div>
              <div className="space-y-1">
                <Label>Manufacturer</Label>
                <Input value={newProduct.manufacturer} onChange={e => setNewProduct(p => ({ ...p, manufacturer: e.target.value }))} placeholder="e.g. Acme Cannabis Co." />
              </div>
              <div className="space-y-1">
                <Label>Strain</Label>
                <Input value={newProduct.strain} onChange={e => setNewProduct(p => ({ ...p, strain: e.target.value }))} placeholder="Indica, Sativa, Hybrid..." />
              </div>
              <div className="space-y-1">
                <Label>THC Content</Label>
                <Input value={newProduct.thcContent} onChange={e => setNewProduct(p => ({ ...p, thcContent: e.target.value }))} placeholder="e.g. 22.5%" />
              </div>
              <div className="space-y-1">
                <Label>CBD Content</Label>
                <Input value={newProduct.cbdContent} onChange={e => setNewProduct(p => ({ ...p, cbdContent: e.target.value }))} placeholder="e.g. 0.8%" />
              </div>
              <div className="space-y-1">
                <Label>Quantity</Label>
                <Input type="number" min="0" value={newProduct.quantity} onChange={e => setNewProduct(p => ({ ...p, quantity: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Unit</Label>
                <Input value={newProduct.unit} onChange={e => setNewProduct(p => ({ ...p, unit: e.target.value }))} placeholder="grams, units, bottles..." />
              </div>
              <div className="space-y-1">
                <Label>Min Quantity (Alert Threshold)</Label>
                <Input type="number" min="0" value={newProduct.minQuantity} onChange={e => setNewProduct(p => ({ ...p, minQuantity: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Batch Number</Label>
                <Input value={newProduct.batchNumber} onChange={e => setNewProduct(p => ({ ...p, batchNumber: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={newProduct.status} onValueChange={v => setNewProduct(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Low Stock">Low Stock</SelectItem>
                    <SelectItem value="Out of Stock">Out of Stock</SelectItem>
                    <SelectItem value="Discontinued">Discontinued</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Description</Label>
                <Textarea value={newProduct.description} onChange={e => setNewProduct(p => ({ ...p, description: e.target.value }))} rows={3} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Product
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
